import { useFeatureFlagEnabled } from '@posthog/react'
import { useRouter } from 'next/router'

import usePartnerOffer from '~/campaigns/hooks/usePartnerOffer'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { applyCouponPercentage } from '~/campaigns/utils/helper'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import {
  EXAM_CAMPAIGN_BY_GEO,
  EXAM_CAMPAIGN_COUNTRY_CODES,
  EXAM_CAMPAIGN_FLAG,
  EXAM_CAMPAIGN_YEARLY_PROMO,
  ExamCampaignGeoOffer,
} from '~/shared/constants/examCampaign.const'
import { useGetStripePriceQuery } from '~/subscription/apis/subscription.api'
import { formatToLocalCurrency } from '~/subscription/util'
import { AppLanguage } from '~/types/locale'

const MONTHS_PER_YEAR = 12

export function useExamCampaignOffer(): ExamCampaignGeoOffer | undefined {
  const flagEnabled = !!useFeatureFlagEnabled(EXAM_CAMPAIGN_FLAG)
  const { data: geoLocation } = useGetUserLocationInfoFromIpQuery()
  const countryCode = geoLocation?.countryCode

  if (!flagEnabled || !countryCode || !EXAM_CAMPAIGN_COUNTRY_CODES.includes(countryCode)) return undefined
  return EXAM_CAMPAIGN_BY_GEO[countryCode]
}

interface ExamCampaignPromotion {
  /** The promotion code for `?coupon=`, set only while Stripe confirms it applies to `priceId`. */
  promoCode?: string
  offer?: ExamCampaignGeoOffer
  applyPromoDiscount: (total: number) => number
}

// Yearly only. Pass no priceId where a partner prices the year, so the partner coupon is never replaced.
export default function useExamCampaignPromotion({
  currency,
  priceId,
}: {
  currency: string
  priceId?: string
}): ExamCampaignPromotion {
  const offer = useExamCampaignOffer()
  const promo = offer?.yearlyPromo ?? EXAM_CAMPAIGN_YEARLY_PROMO
  // currentData, not data: a stale coupon must not show while the price id changes.
  const { currentData } = useGetStripePriceQuery(
    { currency, priceId, promotionId: promo.promotionId },
    { skip: !offer || !priceId },
  )
  const percentOff = currentData?.couponDetails?.percent_off
  const isActive = Boolean(offer && priceId && percentOff)

  return {
    promoCode: isActive ? promo.code : undefined,
    offer: isActive ? offer : undefined,
    applyPromoDiscount: (total) => (isActive ? applyCouponPercentage(total, percentOff) : total),
  }
}

export interface ExamStickerPrices {
  before: string
  price: string
  compact?: boolean
}

// A partner page gets the sticker when its yearly plan is the exam offer: the standard one, or its own (Cox).
export function useExamSticker(partnerDetails?: PartnerDetailsType): ExamStickerPrices | undefined {
  const offer = useExamCampaignOffer()
  const language = useRouter().locale as AppLanguage
  const { yearly, currency, isLoading } = usePartnerOffer(partnerDetails, 1)

  if (!offer || isLoading) return undefined
  if (!yearly.isPartner) return { before: offer.stickerBefore, price: offer.stickerPrice, compact: offer.compact }
  if (!partnerDetails?.examOfferYearly || yearly.base == null) return undefined

  const perMonth = (total: number): string =>
    formatToLocalCurrency(currency ?? '', total / MONTHS_PER_YEAR, language).priceLabel
  return {
    before: perMonth(yearly.base),
    price: perMonth(applyCouponPercentage(yearly.base, yearly.percentOff)),
    compact: offer.compact,
  }
}
